import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Calendar, Users, BookOpen, Gift, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const Membership = () => {
  const { language, t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: '',
    membership_type: 'adult',
    custom_amount: '',
    message: '',
  });

  const benefits = [
    { icon: Calendar, text: t('membership_benefit_1') },
    { icon: BookOpen, text: t('membership_benefit_2') },
    { icon: Users, text: t('membership_benefit_3') },
    { icon: Gift, text: t('membership_benefit_4') },
  ];

  const membershipTypes = [
    { id: 'child', name: language === 'de' ? 'Kinder' : 'Bambini', price: language === 'de' ? 'Beitragsfrei' : 'Gratuito' },
    { id: 'adult', name: language === 'de' ? 'Erwachsene' : 'Adulti', price: '30€ / ' + (language === 'de' ? 'Jahr' : 'anno') },
    { id: 'student', name: language === 'de' ? 'Schüler / Studenten' : 'Studenti', price: '15€ / ' + (language === 'de' ? 'Jahr' : 'anno') },
    { id: 'senior', name: language === 'de' ? 'Rentner' : 'Pensionati', price: '17€ / ' + (language === 'de' ? 'Jahr' : 'anno') },
    { id: 'custom', name: language === 'de' ? 'Individueller Beitrag' : 'Contributo individuale', price: language === 'de' ? 'Eigener Betrag' : 'Importo personalizzato' },
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value) => {
    setFormData({ ...formData, membership_type: value });
  };

  const generatePdf = (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Find the selected membership type label
      const selectedType = membershipTypes.find(t => t.id === formData.membership_type);

      // Build the pre-filled HTML document based on the Anmeldeformular template
      const html = `<!DOCTYPE html>
<html lang="de">
<head>
  <meta charset="UTF-8">
  <title>Beitrittserklärung - ${formData.first_name} ${formData.last_name}</title>
  <style>
    @page { size: A4; margin: 20mm 15mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 10pt; color: #1A1A1A; line-height: 1.4; width: 210mm; margin: 0 auto; padding: 20mm 15mm; background: #fff; }
    .header { text-align: center; margin-bottom: 4mm; }
    .header h1 { font-family: Georgia, 'Times New Roman', serif; font-size: 26pt; font-weight: 700; letter-spacing: 4pt; color: #1A1A1A; margin-bottom: 1mm; }
    .header .subtitle { font-family: Georgia, 'Times New Roman', serif; font-size: 11pt; font-style: italic; color: #444; margin-bottom: 1mm; }
    .header .founded { font-size: 8.5pt; color: #777; letter-spacing: 1pt; text-transform: uppercase; }
    .tricolor { display: flex; height: 3px; margin: 4mm auto; max-width: 100%; }
    .tricolor span { flex: 1; }
    .tricolor .green { background: #008C45; }
    .tricolor .white { background: #F5F5F7; }
    .tricolor .red { background: #D40000; }
    .form-title { text-align: center; margin-bottom: 5mm; }
    .form-title h2 { font-family: Georgia, 'Times New Roman', serif; font-size: 15pt; font-weight: 700; color: #D40000; }
    .section { margin-bottom: 4.5mm; }
    .section-title { font-family: Georgia, 'Times New Roman', serif; font-size: 11pt; font-weight: 700; color: #D40000; padding-bottom: 1mm; border-bottom: 0.5pt solid #D40000; margin-bottom: 3mm; }
    .field-row { display: flex; align-items: flex-end; margin-bottom: 3.5mm; }
    .field-row.split { gap: 6mm; }
    .field { flex: 1; display: flex; align-items: flex-end; }
    .field-label { font-size: 8.5pt; color: #555; white-space: nowrap; margin-right: 2mm; }
    .field-value { flex: 1; border-bottom: 0.5pt solid #999; min-height: 5mm; font-size: 10pt; color: #1A1A1A; padding-bottom: 0.5mm; }
    .field-line { flex: 1; border-bottom: 0.5pt solid #999; min-height: 5mm; }
    .field-short { flex: 0 0 35mm; }
    .membership-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2mm 8mm; margin-bottom: 2mm; }
    .check-item { display: flex; align-items: center; font-size: 9.5pt; line-height: 1.5; }
    .checkbox { display: inline-block; width: 3.8mm; height: 3.8mm; border: 0.5pt solid #1A1A1A; margin-right: 2mm; flex-shrink: 0; text-align: center; font-size: 8pt; line-height: 3.8mm; }
    .checkbox.checked { color: #D40000; font-weight: bold; }
    .check-item .price { color: #555; margin-left: auto; padding-left: 3mm; white-space: nowrap; }
    .individual-row { display: flex; align-items: flex-end; font-size: 9.5pt; margin-top: 1mm; }
    .individual-row .field-value { max-width: 20mm; margin: 0 1mm; border-bottom: 0.5pt solid #999; text-align: center; }
    .individual-row .field-line { max-width: 20mm; margin: 0 1mm; }
    .sepa-auth { display: flex; align-items: flex-start; margin-top: 2mm; font-size: 8.5pt; color: #333; line-height: 1.45; }
    .sepa-auth .checkbox { margin-top: 0.5mm; }
    .consent-text { font-size: 8.5pt; color: #333; line-height: 1.5; text-align: justify; }
    .consent-text p { margin-bottom: 2mm; }
    .signature-area { margin-top: 6mm; }
    .signature-row { display: flex; gap: 10mm; }
    .signature-row .field { flex: 1; }
    .footer { margin-top: 6mm; padding-top: 3mm; border-top: 0.5pt solid #ccc; text-align: center; font-size: 7.5pt; color: #888; line-height: 1.6; }
    .footer .org { font-weight: 600; letter-spacing: 0.5pt; color: #666; }
    .no-print { text-align: center; margin-bottom: 8mm; }
    @media print {
      body { padding: 0; width: auto; background: none; }
      .no-print { display: none; }
      .tricolor span, .checkbox, .section-title, .form-title h2 { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .checkbox.checked { -webkit-print-color-adjust: exact; print-color-adjust: exact; color: #D40000; }
    }
  </style>
</head>
<body>
  <div class="no-print">
    <button onclick="window.print()" style="padding: 10px 30px; font-size: 14px; background: #D40000; color: white; border: none; cursor: pointer; font-family: Arial, sans-serif; letter-spacing: 0.5px;">
      \u{1F5A8} ${language === 'de' ? 'Drucken / Als PDF speichern' : 'Stampa / Salva come PDF'}
    </button>
  </div>

  <div class="header">
    <h1>AMITALIA e.V.</h1>
    <div class="subtitle">Deutsch-Italienischer Freundschaftsverein</div>
    <div class="founded">Gegr&uuml;ndet 2011 &middot; Karlsdorf-Neuthard</div>
  </div>
  <div class="tricolor"><span class="green"></span><span class="white"></span><span class="red"></span></div>
  <div class="form-title"><h2>Beitrittserkl&auml;rung / Domanda di adesione</h2></div>

  <div class="section">
    <div class="section-title">1 &ensp; Pers&ouml;nliche Daten / Dati personali</div>
    <div class="field-row split">
      <div class="field"><span class="field-label">Vorname / Nome:</span><span class="field-value">${formData.first_name}</span></div>
      <div class="field"><span class="field-label">Nachname / Cognome:</span><span class="field-value">${formData.last_name}</span></div>
    </div>
    <div class="field-row">
      <div class="field"><span class="field-label">Geburtsdatum / Data di nascita:</span><span class="field-line"></span></div>
    </div>
    <div class="field-row">
      <div class="field"><span class="field-label">Stra&szlig;e, Hausnr. / Via, n.:</span><span class="field-value">${formData.address}</span></div>
    </div>
    <div class="field-row split">
      <div class="field field-short"><span class="field-label">PLZ / CAP:</span><span class="field-value">${formData.postal_code}</span></div>
      <div class="field"><span class="field-label">Ort / Citt&agrave;:</span><span class="field-value">${formData.city}</span></div>
    </div>
    <div class="field-row">
      <div class="field"><span class="field-label">Land / Paese:</span><span class="field-value">${formData.country}</span></div>
    </div>
    <div class="field-row split">
      <div class="field"><span class="field-label">Telefon / Telefono:</span><span class="field-value">${formData.phone || ''}</span></div>
      <div class="field"><span class="field-label">E-Mail:</span><span class="field-value">${formData.email}</span></div>
    </div>
  </div>

  <div class="section">
    <div class="section-title">2 &ensp; Mitgliedsbeitrag / Quota associativa</div>
    <div class="membership-grid">
      <div class="check-item"><span class="checkbox${formData.membership_type === 'child' ? ' checked' : ''}">${formData.membership_type === 'child' ? 'X' : ''}</span>Kinder / Bambini<span class="price">Beitragsfrei / Gratuito</span></div>
      <div class="check-item"><span class="checkbox${formData.membership_type === 'adult' ? ' checked' : ''}">${formData.membership_type === 'adult' ? 'X' : ''}</span>Erwachsene / Adulti<span class="price">30&thinsp;&euro; / Jahr / anno</span></div>
      <div class="check-item"><span class="checkbox${formData.membership_type === 'student' ? ' checked' : ''}">${formData.membership_type === 'student' ? 'X' : ''}</span>Sch&uuml;ler &amp; Studenten / Studenti<span class="price">15&thinsp;&euro; / Jahr / anno</span></div>
      <div class="check-item"><span class="checkbox${formData.membership_type === 'senior' ? ' checked' : ''}">${formData.membership_type === 'senior' ? 'X' : ''}</span>Rentner / Pensionati<span class="price">17&thinsp;&euro; / Jahr / anno</span></div>
    </div>
    <div class="individual-row">
      <span class="checkbox${formData.membership_type === 'custom' ? ' checked' : ''}">${formData.membership_type === 'custom' ? 'X' : ''}</span>
      <span>Individueller Beitrag / Contributo individuale:</span>
      <span class="field-value">${formData.membership_type === 'custom' ? formData.custom_amount : ''}</span>
      <span>&euro;</span>
    </div>
  </div>

  <div class="section">
    <div class="section-title">3 &ensp; Bankverbindung f&uuml;r SEPA-Lastschrift / Coordinate bancarie per addebito SEPA</div>
    <div class="field-row"><div class="field"><span class="field-label">Kontoinhaber / Intestatario del conto:</span><span class="field-line"></span></div></div>
    <div class="field-row"><div class="field"><span class="field-label">IBAN:</span><span class="field-line"></span></div></div>
    <div class="field-row"><div class="field"><span class="field-label">BIC / SWIFT:</span><span class="field-line"></span></div></div>
    <div class="sepa-auth">
      <span class="checkbox"></span>
      <span>Ich erm&auml;chtige Amitalia e.V., den Mitgliedsbeitrag j&auml;hrlich von meinem Konto einzuziehen. / Autorizzo Amitalia e.V. a prelevare annualmente la quota associativa dal mio conto.</span>
    </div>
  </div>

  <div class="section">
    <div class="section-title">4 &ensp; Einverst&auml;ndniserkl&auml;rung / Dichiarazione di consenso</div>
    <div class="consent-text">
      <p>Mit meiner Unterschrift erkl&auml;re ich meinen Beitritt zum Verein Amitalia e.V. und erkenne die Satzung des Vereins an. Ich bin damit einverstanden, dass meine Daten zum Zweck der Mitgliederverwaltung gespeichert und verarbeitet werden.</p>
      <p>Con la mia firma dichiaro la mia adesione all&rsquo;associazione Amitalia e.V. e riconosco lo statuto dell&rsquo;associazione. Acconsento alla memorizzazione e all&rsquo;elaborazione dei miei dati ai fini della gestione dei soci.</p>
    </div>
  </div>

  <div class="signature-area">
    <div class="signature-row">
      <div class="field"><span class="field-label">Ort, Datum / Luogo, data:</span><span class="field-line"></span></div>
      <div class="field"><span class="field-label">Unterschrift / Firma:</span><span class="field-line"></span></div>
    </div>
  </div>

  <div class="footer">
    <div class="org">Amitalia e.V. &middot; Karlsdorf-Neuthard &middot; www.amitalia.de</div>
    <div>Bitte senden Sie das ausgef&uuml;llte Formular per Post oder E-Mail an: info@amitalia.de<br>Si prega di inviare il modulo compilato per posta o e-mail a: info@amitalia.de</div>
  </div>
</body>
</html>`;

      // Open in new window for printing
      const printWindow = window.open('', '_blank');
      printWindow.document.write(html);
      printWindow.document.close();

      toast.success(language === 'de'
        ? 'Formular wurde erstellt! Bitte drucken oder als PDF speichern.'
        : 'Modulo creato! Si prega di stampare o salvare come PDF.');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(t('form_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="overflow-hidden" data-testid="membership-page">
      {/* Hero Section */}
      <section className="relative py-24 bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold mb-6" data-testid="membership-title">
              {t('membership_title')}
            </h1>
            <p className="text-lg text-foreground/70">
              {t('membership_subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold mb-12 text-center">
            {t('membership_benefits_title')}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
                data-testid={`benefit-${index}`}
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-ferrari/10 rounded-full flex items-center justify-center">
                  <benefit.icon className="w-8 h-8 text-ferrari" />
                </div>
                <p className="text-foreground/70">{benefit.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Membership Types */}
      <section className="py-20 bg-[#F5F5F7]" data-testid="types-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold mb-12 text-center">
            {t('membership_types_title')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
            {membershipTypes.map((type, index) => (
              <motion.div
                key={type.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white p-6 luxury-shadow text-center ${
                  type.id === 'adult' ? 'border-2 border-ferrari' : 'border border-black/5'
                }`}
                data-testid={`membership-type-${type.id}`}
              >
                {type.id === 'adult' && (
                  <span className="inline-block bg-ferrari text-white text-xs font-semibold px-3 py-1 mb-4">
                    {language === 'de' ? 'STANDARD' : 'STANDARD'}
                  </span>
                )}
                <h3 className="font-heading text-lg font-semibold mb-2">{type.name}</h3>
                <div className="font-accent text-xl text-ferrari">{type.price}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20" data-testid="application-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl font-semibold mb-8 text-center">
              {t('membership_form_title')}
            </h2>

            <form onSubmit={generatePdf} className="space-y-6" data-testid="membership-form">
              {/* Name Fields */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="first_name">{t('form_first_name')} *</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="border-0 border-b border-black/10 rounded-none focus:border-ferrari px-0"
                    data-testid="input-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">{t('form_last_name')} *</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    className="border-0 border-b border-black/10 rounded-none focus:border-ferrari px-0"
                    data-testid="input-last-name"
                  />
                </div>
              </div>

              {/* Contact Fields */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('form_email')} *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="border-0 border-b border-black/10 rounded-none focus:border-ferrari px-0"
                    data-testid="input-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">{t('form_phone')}</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="border-0 border-b border-black/10 rounded-none focus:border-ferrari px-0"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              {/* Address */}
              <div className="space-y-2">
                <Label htmlFor="address">{t('form_address')} *</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  className="border-0 border-b border-black/10 rounded-none focus:border-ferrari px-0"
                  data-testid="input-address"
                />
              </div>

              {/* City & Postal */}
              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="city">{t('form_city')} *</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="border-0 border-b border-black/10 rounded-none focus:border-ferrari px-0"
                    data-testid="input-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postal_code">{t('form_postal')} *</Label>
                  <Input
                    id="postal_code"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleChange}
                    required
                    className="border-0 border-b border-black/10 rounded-none focus:border-ferrari px-0"
                    data-testid="input-postal"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">{t('form_country')} *</Label>
                  <Input
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                    className="border-0 border-b border-black/10 rounded-none focus:border-ferrari px-0"
                    data-testid="input-country"
                  />
                </div>
              </div>

              {/* Membership Type */}
              <div className="space-y-2">
                <Label>{t('form_membership_type')} *</Label>
                <Select value={formData.membership_type} onValueChange={handleSelectChange}>
                  <SelectTrigger className="w-full" data-testid="select-membership-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {membershipTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name} - {type.price}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Custom Amount Field */}
              {formData.membership_type === 'custom' && (
                <div className="space-y-2">
                  <Label htmlFor="custom_amount">{language === 'de' ? 'Ihr Beitrag (€)' : 'Il tuo contributo (€)'} *</Label>
                  <Input
                    id="custom_amount"
                    name="custom_amount"
                    type="number"
                    min="1"
                    value={formData.custom_amount}
                    onChange={handleChange}
                    required={formData.membership_type === 'custom'}
                    placeholder={language === 'de' ? 'Betrag eingeben' : 'Inserisci importo'}
                    className="border-0 border-b border-black/10 rounded-none focus:border-ferrari px-0"
                    data-testid="input-custom-amount"
                  />
                </div>
              )}

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message">{t('form_message')}</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="border-0 border-b border-black/10 rounded-none focus:border-ferrari px-0 resize-none"
                  data-testid="input-message"
                />
              </div>

              {/* GDPR Consent */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="privacy-consent-membership"
                  className="mt-1 accent-[hsl(0,100%,42%)]"
                  data-testid="privacy-consent"
                />
                <Label htmlFor="privacy-consent-membership" className="text-sm text-foreground/70 leading-relaxed cursor-pointer">
                  {language === 'de'
                    ? 'Ihre Daten werden nicht übertragen. Das Formular wird lokal in Ihrem Browser erstellt.'
                    : 'I vostri dati non vengono trasmessi. Il modulo viene creato localmente nel vostro browser.'}
                </Label>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full disabled:opacity-50 inline-flex items-center justify-center gap-2"
                data-testid="submit-membership-btn"
              >
                <Download size={18} />
                {loading ? '...' : (language === 'de' ? 'Formular erstellen & drucken' : 'Crea modulo e stampa')}
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Membership;
